import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Eye,
  Edit3,
  Trash2,
  Clock,
  LogOut,
  ExternalLink,
} from "lucide-react";
import {
  authMe,
  authLogout,
  adminListArticles,
  adminDeleteArticle,
  resolveImage,
} from "@/lib/blogApi";
import TopicSuggestionModal from "@/components/TopicSuggestionModal";

const RED = "#D41F30";

const STATUS_BADGES = {
  pending_review: { label: "Pending review", color: "bg-amber-100 text-amber-800" },
  published: { label: "Published", color: "bg-green-100 text-green-800" },
  archived: { label: "Archived", color: "bg-gray-100 text-gray-600" },
};

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [articles, setArticles] = useState([]);
  const [tab, setTab] = useState("pending_review");
  const [loading, setLoading] = useState(true);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await adminListArticles();
      setArticles(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    authMe()
      .then((u) => {
        if (!u.is_admin) {
          navigate("/login", { replace: true });
          return;
        }
        setUser(u);
        refresh();
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate]);

  const handleGenerate = () => {
    setError("");
    setTopicModalOpen(true);
  };

  const handleTopicComplete = (article) => {
    setTopicModalOpen(false);
    navigate(`/admin/articles/${article.id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await adminDeleteArticle(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleLogout = async () => {
    await authLogout().catch(() => {});
    navigate("/login", { replace: true });
  };

  const filtered = articles.filter((a) => (tab === "all" ? true : a.status === tab));
  const counts = {
    all: articles.length,
    pending_review: articles.filter((a) => a.status === "pending_review").length,
    published: articles.filter((a) => a.status === "published").length,
    archived: articles.filter((a) => a.status === "archived").length,
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400" data-testid="admin-loading">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D41F30] flex items-center justify-center">
              <span className="text-white font-black text-sm">CW</span>
            </div>
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-gray-500">
                CityWorth Admin
              </div>
              <div className="text-base font-display font-bold text-gray-900">Blog Studio</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/blog"
              target="_blank"
              className="hidden md:inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#D41F30]"
              data-testid="admin-view-public"
            >
              View public blog <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <div className="text-sm text-gray-600 hidden sm:block">{user.email}</div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-[#D41F30] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-2">
              Articles
            </h1>
            <p className="text-gray-600">
              The AI drafts a new article each morning. Review, polish, then publish.
            </p>
          </div>
          <button
            onClick={handleGenerate}
            data-testid="admin-generate-btn"
            className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] disabled:opacity-60 disabled:cursor-not-allowed text-white border-2 border-[#D41F30] rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <Sparkles className="mr-2 w-4 h-4" />
            Generate new article
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="admin-tabs">
          {[
            { id: "pending_review", label: "Pending review" },
            { id: "published", label: "Published" },
            { id: "archived", label: "Archived" },
            { id: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                tab === t.id
                  ? "bg-[#D41F30] text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {t.label} <span className="opacity-60">({counts[t.id]})</span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 mb-2">No articles in this view yet.</p>
              <p className="text-sm text-gray-400">Click "Generate new article" to create one.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs font-bold tracking-wider uppercase text-gray-500">
                  <th className="px-6 py-3">Article</th>
                  <th className="px-6 py-3 hidden md:table-cell">Category</th>
                  <th className="px-6 py-3 hidden lg:table-cell">Created</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((a) => {
                  const badge = STATUS_BADGES[a.status] || STATUS_BADGES.pending_review;
                  return (
                    <tr key={a.id} className="hover:bg-gray-50" data-testid={`row-${a.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {a.image_url && (
                            <img
                              src={resolveImage(a.image_url)}
                              alt=""
                              className="w-16 h-12 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 line-clamp-1">{a.title}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              <Clock className="inline w-3 h-3 mr-1" />
                              {a.reading_time} min read
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">
                        {a.category}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 hidden lg:table-cell">
                        {formatDate(a.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${badge.color}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {a.status === "published" && (
                            <Link
                              to={`/blog/${a.slug}`}
                              target="_blank"
                              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900"
                              title="View live"
                              data-testid={`view-${a.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                          )}
                          <Link
                            to={`/admin/articles/${a.id}`}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#D41F30]"
                            title="Edit"
                            data-testid={`edit-${a.id}`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDelete(a.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"
                            title="Delete"
                            data-testid={`delete-${a.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <TopicSuggestionModal
        open={topicModalOpen}
        onClose={() => setTopicModalOpen(false)}
        onComplete={handleTopicComplete}
      />
    </div>
  );
}
